import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, Sparkles, Heart, Leaf, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom images
const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_sound-bath-hub/artifacts/013ytm7m_FB_IMG_1770979375236.jpg",
  soundHealing: "https://customer-assets.emergentagent.com/job_9d2782f9-5276-4e92-beba-9f63a63d9a7f/artifacts/h7wg6aqw_file_0000000000947230a410da9a77c2c8e9.png",
  soundExperiences: "https://customer-assets.emergentagent.com/job_9d2782f9-5276-4e92-beba-9f63a63d9a7f/artifacts/x3xteiqx_Screenshot_20260413_174546_Chrome.jpg",
  sway: "https://customer-assets.emergentagent.com/job_sound-bath-hub/artifacts/gh68m4t9_file_000000008a5c720a996949e446233e25%20%281%29.png",
  massage: "https://customer-assets.emergentagent.com/job_sound-bath-hub/artifacts/ev5qc975_FB_IMG_1770979416407.jpg",
  texture: "https://customer-assets.emergentagent.com/job_sound-bath-hub/artifacts/i26ef6lk_FB_IMG_1770979388143.jpg",
};

const INSTA_IMAGES = [
  "https://customer-assets-rejwkqb3.emergentagent.net/job_9d2782f9-5276-4e92-beba-9f63a63d9a7f/artifacts/ta2szmkb_file_0000000000947230a410da9a77c2c8e9.png",
  "https://customer-assets-rejwkqb3.emergentagent.net/job_9d2782f9-5276-4e92-beba-9f63a63d9a7f/artifacts/vmj32u17_IMG-20260826-WA0053.webp",
  "https://customer-assets-rejwkqb3.emergentagent.net/job_9d2782f9-5276-4e92-beba-9f63a63d9a7f/artifacts/2bs82ywh_file_000000009670723086d11c03fb0af355.png",
  "https://customer-assets-rejwkqb3.emergentagent.net/job_9d2782f9-5276-4e92-beba-9f63a63d9a7f/artifacts/0yge4pwh_IMG-20260826-WA0054.webp",
  "https://customer-assets-rejwkqb3.emergentagent.net/job_9d2782f9-5276-4e92-beba-9f63a63d9a7f/artifacts/me9gpghq_file_000000001ef471f8805d3e317caa5f32.png",
  "https://customer-assets.emergentagent.com/job_sound-bath-hub/artifacts/i26ef6lk_FB_IMG_1770979388143.jpg",
];

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

export default function HomePage() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    // Seed data on first load
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (error) {
        console.error("Seed data error:", error);
      }
    };
    seedData();

    // Fetch testimonials
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials?featured_only=true`);
        setTestimonials(response.data);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      }
    };
    fetchTestimonials();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.hero}
            alt="Sound healing atmosphere"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />
        </div>

        <div className="container-custom relative z-10 pt-32 pb-24">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-3xl"
          >
            <motion.p
              variants={fadeInUp}
              className="text-xs tracking-[0.3em] uppercase text-primary mb-6"
            >
              Holistic Healing & Wellness
            </motion.p>
            <motion.h1
              variants={fadeInUp}
              className="heading-xl mb-8 text-foreground"
            >
              Find Your <em className="italic">Stillness</em>
              <br />
              Through Sound & Touch
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="body-text text-muted-foreground max-w-xl mb-12"
            >
              Experience the transformative power of holistic healing. Through 
              therapeutic massage, Reiki, and immersive sound journeys, I guide 
              you back to balance and inner peace.
            </motion.p>
            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/booking">
                <Button
                  size="lg"
                  className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground px-10 py-7 text-xs tracking-[0.2em] uppercase btn-animate"
                  data-testid="hero-book-btn"
                >
                  Book a Session
                  <ArrowRight className="ml-3 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/sway-with-sound">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground px-10 py-7 text-xs tracking-[0.2em] uppercase btn-animate"
                  data-testid="hero-sway-btn"
                >
                  Explore Sway with Sound
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
        >
          <div className="flex flex-col items-center gap-2">
            <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
              Scroll
            </span>
            <div className="w-px h-12 bg-border relative overflow-hidden">
              <motion.div
                animate={{ y: [0, 48, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="w-full h-4 bg-primary"
              />
            </div>
          </div>
        </motion.div>
      </section>

      {/* About Section */}
      <section className="section-spacing bg-muted" data-testid="about-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
                About Me
              </p>
              <h2 className="heading-lg mb-8 text-foreground">
                Healing Hands,<br />
                <em className="italic">Open Heart</em>
              </h2>
              <div className="space-y-6 body-text text-muted-foreground">
                <p>
                  Welcome to my healing space. I'm a certified holistic therapist 
                  specialising in therapeutic massage, Reiki energy healing, and 
                  sound therapy.
                </p>
                <p>
                  My journey into healing began with my own need for deep rest and 
                  nervous system regulation. Through years of training and personal 
                  practice, I've developed a unique approach that combines ancient 
                  wisdom with modern understanding of the body-mind connection.
                </p>
                <p>
                  Every session is tailored to your needs, creating a safe container 
                  for you to release, restore, and reconnect with your authentic self.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-portrait relative">
                <img
                  src={IMAGES.massage}
                  alt="Healing massage"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-background p-8 shadow-lg max-w-xs">
                <div className="flex items-center gap-2 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={`star-${i}`} className="w-4 h-4 fill-secondary text-secondary" />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground italic">
                  "A truly transformative experience. I've never felt so peaceful."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-spacing" data-testid="services-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
              What I Offer
            </p>
            <h2 className="heading-lg text-foreground">
              Paths to <em className="italic">Healing</em>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Sacred Sounds Studio */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="group relative overflow-hidden bg-muted"
              data-testid="service-card-massage"
            >
              <div className="aspect-landscape relative overflow-hidden">
                <img
                  src={IMAGES.soundHealing}
                  alt="Sacred Sounds Studio"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-foreground/20" />
              </div>
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Heart className="w-5 h-5 text-primary" />
                  <span className="text-xs tracking-[0.2em] uppercase text-primary">
                    1:1 Sessions
                  </span>
                </div>
                <h3 className="font-serif text-2xl mb-4 text-foreground">
                  Healing Treatments
                </h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Massage combined with Reiki, vibroacoustic healing, 
                  and sound therapy. Tailored to your needs.
                </p>
                <Link to="/treatments">
                  <Button
                    variant="outline"
                    className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground text-xs tracking-[0.15em] uppercase btn-animate"
                    data-testid="explore-massage-btn"
                  >
                    Explore Treatments
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Sound Experiences */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="group relative overflow-hidden bg-muted"
              data-testid="service-card-experiences"
            >
              <div className="aspect-landscape relative overflow-hidden">
                <img
                  src={IMAGES.soundExperiences}
                  alt="Sound Experiences"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-foreground/20" />
              </div>
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles className="w-5 h-5 text-primary" />
                  <span className="text-xs tracking-[0.2em] uppercase text-primary">
                    Solo & Groups
                  </span>
                </div>
                <h3 className="font-serif text-2xl mb-4 text-foreground">
                  Sound Experiences
                </h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Immersive sound journeys in Sacred Sounds Studio or brought to you. 
                  From private sessions to group gatherings.
                </p>
                <Link to="/sound-experiences">
                  <Button
                    variant="outline"
                    className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground text-xs tracking-[0.15em] uppercase btn-animate"
                    data-testid="explore-experiences-btn"
                  >
                    Explore Experiences
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Sway with Sound */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="group relative overflow-hidden bg-muted"
              data-testid="service-card-sway"
            >
              <div className="aspect-landscape relative overflow-hidden">
                <img
                  src={IMAGES.sway}
                  alt="Sway with Sound"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-foreground/20" />
              </div>
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Leaf className="w-5 h-5 text-primary" />
                  <span className="text-xs tracking-[0.2em] uppercase text-primary">
                    Group Classes
                  </span>
                </div>
                <h3 className="font-serif text-2xl mb-4 text-foreground">
                  Sway with Sound
                </h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Float in aerial hammocks while being bathed in healing sound 
                  frequencies. A unique nervous system reset.
                </p>
                <Link to="/sway-with-sound">
                  <Button
                    variant="outline"
                    className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground text-xs tracking-[0.15em] uppercase btn-animate"
                    data-testid="explore-sway-btn"
                  >
                    Explore Sway
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-spacing bg-muted" data-testid="testimonials-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
              Client Love
            </p>
            <h2 className="heading-lg text-foreground">
              Words from the <em className="italic">Heart</em>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="testimonial-card bg-background p-8 border border-border"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex items-center gap-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={`tstar-${i}`} className="w-4 h-4 fill-secondary text-secondary" />
                  ))}
                </div>
                <p className="body-text text-muted-foreground mb-6 italic">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center">
                    <span className="font-serif text-lg text-primary">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-foreground text-sm">
                      {testimonial.name}
                    </p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {testimonial.service_type?.replace("_", " ") || "Client"}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Instagram Section */}
      <section className="section-spacing" data-testid="instagram-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <Instagram className="w-5 h-5 text-primary" />
              <p className="text-xs tracking-[0.3em] uppercase text-primary">
                @soundhealingmassage
              </p>
            </div>
            <h2 className="heading-lg text-foreground">
              Follow the <em className="italic">Journey</em>
            </h2>
          </motion.div>

          <a
            href="https://www.instagram.com/soundhealingmassage/"
            target="_blank"
            rel="noopener noreferrer"
            className="block"
            data-testid="instagram-grid-link"
          >
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
              {INSTA_IMAGES.map((img, index) => (
                <motion.div
                  key={`insta-${index}`}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="group relative aspect-square overflow-hidden"
                >
                  <img
                    src={img}
                    alt="Sacred Sounds Studio"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/30 transition-all duration-300 flex items-center justify-center">
                    <Instagram className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </motion.div>
              ))}
            </div>
          </a>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mt-10"
          >
            <a
              href="https://www.instagram.com/soundhealingmassage/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="outline"
                className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground px-10 py-6 text-xs tracking-[0.2em] uppercase btn-animate"
                data-testid="instagram-follow-btn"
              >
                <Instagram className="mr-2 w-4 h-4" />
                Follow on Instagram
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <img
            src={IMAGES.texture}
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="heading-lg mb-6 text-foreground">
              Ready to Begin Your<br />
              <em className="italic">Healing Journey?</em>
            </h2>
            <p className="body-text text-muted-foreground mb-10">
              Book your session today and take the first step towards 
              deep relaxation and inner peace.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/booking">
                <Button
                  size="lg"
                  className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground px-12 py-7 text-xs tracking-[0.2em] uppercase btn-animate"
                  data-testid="cta-book-btn"
                >
                  Book a Session
                  <ArrowRight className="ml-3 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/gift-vouchers">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground px-12 py-7 text-xs tracking-[0.2em] uppercase btn-animate"
                  data-testid="cta-gift-btn"
                >
                  Gift a Session
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
